export const AGENT_READINESS_DIMENSIONS = [
  { key: 'project_clarity', layer: 'understanding', weight: 0.15 },
  { key: 'resource_discoverability', layer: 'understanding', weight: 0.15 },
  { key: 'documentation_quality', layer: 'understanding', weight: 0.2 },
  { key: 'machine_interface', layer: 'operability', weight: 0.2 },
  { key: 'workflow_completeness', layer: 'operability', weight: 0.15 },
  { key: 'execution_verifiability', layer: 'operability', weight: 0.15 },
] as const;

export const AGENT_READINESS_CHECK_KEYS = [
  'docs',
  'quickstart',
  'api_reference',
  'openapi',
  'cli',
  'cli_non_interactive',
  'cli_structured_output',
  'sdk',
  'mcp',
  'webhooks',
  'structured_import_export',
  'authentication',
  'request_examples',
  'response_examples',
  'error_documentation',
  'rate_limits',
  'version_information',
  'changelog',
  'llms_txt',
  'sitemap',
  'success_verification',
  // Agent-enablement signals: products whose operability surface for agents
  // is code + skills (templates, frameworks) rather than HTTP APIs.
  'agent_tooling_artifacts',
  'agent_native_positioning',
] as const;

export const AGENT_READINESS_STAGE_BUDGETS = {
  discovery: { externalActions: 15, pageReads: 10, maxSourceDepth: 3 },
  developerAudit: { externalActions: 20, pageReads: 15, maxPagesPerInterface: 5 },
  validation: { externalActions: 8 },
} as const;

export type AgentReadinessDimensionKey = (typeof AGENT_READINESS_DIMENSIONS)[number]['key'];
export type AgentReadinessCheckKey = (typeof AGENT_READINESS_CHECK_KEYS)[number];
export type AgentReadinessClassification =
  | 'human_operated'
  | 'agent_assisted'
  | 'agent_compatible'
  | 'agent_native';

export type AgentReadinessDimensionScores = Record<AgentReadinessDimensionKey, number>;

export type AgentReadinessScores = {
  understandingScore: number;
  operabilityScore: number;
  overallScore: number;
};

function roundHalfUp(value: number) {
  return Math.floor(value + 0.5);
}

export function calculateAgentReadinessScores(scores: AgentReadinessDimensionScores): AgentReadinessScores {
  const keys = AGENT_READINESS_DIMENSIONS.map((dimension) => dimension.key);
  if (keys.some((key) => !Object.hasOwn(scores, key))) {
    throw new Error('Agent Readiness requires all six benchmark dimensions');
  }
  for (const key of keys) {
    const score = scores[key];
    if (!Number.isFinite(score) || score < 0 || score > 100) {
      throw new Error(`Dimension ${key} must be between 0 and 100`);
    }
  }

  const understandingScore = roundHalfUp(
    scores.project_clarity * 0.3
      + scores.resource_discoverability * 0.3
      + scores.documentation_quality * 0.4,
  );
  const operabilityScore = roundHalfUp(
    scores.machine_interface * 0.4
      + scores.workflow_completeness * 0.3
      + scores.execution_verifiability * 0.3,
  );

  return {
    understandingScore,
    operabilityScore,
    overallScore: roundHalfUp((understandingScore + operabilityScore) / 2),
  };
}

type ClassificationSignals = {
  verifiedMachineInterface: boolean;
  verifiedAgentTooling: boolean;
  verifiedEndToEndWorkflow: boolean;
  verifiedStructuredResult: boolean;
  verifiedSuccessDetection: boolean;
  machineInterfaceScore: number;
};

export function classifyAgentReadiness(
  scores: AgentReadinessScores,
  signals: ClassificationSignals,
): AgentReadinessClassification {
  // An operability surface is either a machine interface (API/CLI/SDK/MCP/
  // webhooks, with substance) or documented agent tooling (skills, AGENTS.md,
  // code distribution) — code-first products expose the latter, not the former.
  const machineUsable = signals.verifiedMachineInterface && signals.machineInterfaceScore >= 50;
  if (!machineUsable && !signals.verifiedAgentTooling) {
    return scores.understandingScore >= 50 ? 'agent_assisted' : 'human_operated';
  }
  if (
    scores.overallScore >= 85
    && scores.operabilityScore >= 85
    && machineUsable
    && signals.verifiedEndToEndWorkflow
    && signals.verifiedStructuredResult
    && signals.verifiedSuccessDetection
  ) {
    return 'agent_native';
  }
  if (signals.verifiedEndToEndWorkflow) return 'agent_compatible';
  return 'agent_assisted';
}
